import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase-config';
import { EventMessage, EVENT_MESSAGES_COLLECTION } from '../models/event-message';

const EMPTY_MESSAGE: EventMessage = { active: false, message: '' };

const parseEventMessage = (data: Record<string, unknown> | undefined): EventMessage => ({
  active: typeof data?.active === 'boolean' ? data.active : false,
  message: typeof data?.message === 'string' ? data.message : '',
});

// Real-time subscription to the per-event message doc (event_messages/{eventId}),
// mirroring usePlatformConfig's onSnapshot pattern. Returns EMPTY_MESSAGE while
// there is no event id, no doc, or on error, so callers can safely gate on
// `active && message`.
const useEventMessage = (eventId?: string): EventMessage => {
  const [eventMessage, setEventMessage] = useState<EventMessage>(EMPTY_MESSAGE);

  useEffect(() => {
    if (!eventId) {
      setEventMessage(EMPTY_MESSAGE);
      return undefined;
    }

    const ref = doc(db, EVENT_MESSAGES_COLLECTION, eventId);

    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        setEventMessage(snapshot.exists() ? parseEventMessage(snapshot.data()) : EMPTY_MESSAGE);
      },
      (error) => {
        console.error('Event message listener error:', error);
        setEventMessage(EMPTY_MESSAGE);
      },
    );

    return unsubscribe;
  }, [eventId]);

  return eventMessage;
};

export default useEventMessage;
